'use client';

import { c as _c } from "react/compiler-runtime";
import { Accordion, AccordionContent, AccordionItem } from '@achra/ui/accordion';
import { cn } from '@achra/ui/lib/utils';
import { Separator } from '@achra/ui/separator';
import { utc } from '@date-fns/utc';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { format, parseISO } from 'date-fns';
import { useId } from 'react';
import { AccountTransactionDirection } from '@/modules/__generated__/graphql/switchboard-generated';
import { usLocalizedNumber } from '@/modules/shared/lib/humanization';
import { TxHash } from '../tx-hash';
import { ExpenseArrow } from './expense-arrow';
import { WalletInfo } from './wallet-info';
import type { TransactionProps } from './transaction';
interface MobileTransactionProps extends TransactionProps {
  // prop to allow the component to be expanded by default (used in the storybook)
  defaultExpanded?: boolean;
}
function MobileTransaction(t0) {
  const $ = _c(53);
  const {
    label,
    date,
    toDate,
    txHash,
    counterPartyName,
    counterPartyAddress,
    amount,
    defaultExpanded: t1,
    highlightPositiveAmounts,
    direction
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  const id = useId();
  const isIncomingTransaction = direction === AccountTransactionDirection.Inflow;
  let t2;
  if ($[0] !== date || $[1] !== toDate) {
    t2 = toDate ? <>from {format(parseISO(date), "dd-MMM-yyyy", {
        in: utc
      })}<br />to {format(parseISO(toDate), "dd-MMM-yyyy", {
        in: utc
      })}</> : `${format(parseISO(date), "dd-MMM-yyyy HH:mm", {
      in: utc
    })} UTC`;
    $[0] = date;
    $[1] = toDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formattedDate = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("focus-visible:border-ring focus-visible:ring-ring/50 group relative flex flex-1 transition-all outline-none focus-visible:ring-[3px]", "p-2 data-[state=open]:pb-0");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = label === "Swap";
  let t5;
  if ($[4] !== isIncomingTransaction || $[5] !== t4) {
    t5 = <div className="pt-2 pr-2"><ExpenseArrow isIncoming={isIncomingTransaction} isSwap={t4} /></div>;
    $[4] = isIncomingTransaction;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label) {
    t6 = <div>{label}</div>;
    $[7] = label;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== formattedDate) {
    t7 = <div className="text-foreground/30">{formattedDate}</div>;
    $[9] = formattedDate;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <div className="flex flex-col text-left text-xs/4.5 font-medium">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = amount > 0 && !!highlightPositiveAmounts ? "text-status-success" : "text-foreground";
  let t10;
  if ($[14] !== t9) {
    t10 = cn("flex items-baseline justify-end gap-1 pt-1 text-sm/5.5 font-semibold", t9);
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = isIncomingTransaction ? "+" : "-";
  let t12;
  if ($[16] !== t11) {
    t12 = <span>{t11}</span>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== amount) {
    t13 = usLocalizedNumber(amount);
    $[18] = amount;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-foreground/30 text-xs/4.5 font-medium">USD</span>;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t10 || $[22] !== t12 || $[23] !== t13) {
    t15 = <div className={t10}>{t12}{t13}{t14}</div>;
    $[21] = t10;
    $[22] = t12;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15 || $[26] !== t8) {
    t16 = <div className="flex w-full items-start justify-between">{t8}{t15}</div>;
    $[25] = t15;
    $[26] = t8;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bg-accent absolute top-0 right-0 flex size-4 items-center justify-center rounded-tr-lg rounded-br-lg"><span data-sign-state="collapsed" className="group-data-[state=closed]:inline-block group-data-[state=open]:hidden">+</span><span data-sign-state="expanded" className="group-data-[state=closed]:hidden group-data-[state=open]:inline-block">-</span></div>;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t16 || $[30] !== t5) {
    t18 = <AccordionPrimitive.Header className="flex"><AccordionPrimitive.Trigger data-slot="accordion-trigger" className={t3}>{t5}{t16}{t17}</AccordionPrimitive.Trigger></AccordionPrimitive.Header>;
    $[29] = t16;
    $[30] = t5;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== txHash) {
    t19 = <div className="pl-8"><TxHash txHash={txHash} /></div>;
    $[32] = txHash;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Separator className="my-2" />;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const t21 = isIncomingTransaction ? "Sender Address" : "Recipient Address";
  let t22;
  if ($[35] !== t21) {
    t22 = <div className="text-foreground/50 flex items-center justify-center pr-3.5 text-xs/4.5 font-medium">{t21}</div>;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== counterPartyAddress || $[38] !== counterPartyName) {
    t23 = <WalletInfo name={counterPartyName} address={counterPartyAddress} />;
    $[37] = counterPartyAddress;
    $[38] = counterPartyName;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t22 || $[41] !== t23) {
    t24 = <div className="px-2 pb-2">{t20}<div className="flex justify-between pr-4 pb-2 pl-2">{t22}{t23}</div></div>;
    $[40] = t22;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t19 || $[44] !== t24) {
    t25 = <AccordionContent className="p-0">{t19}{t24}</AccordionContent>;
    $[43] = t19;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== id || $[47] !== t18 || $[48] !== t25) {
    t26 = <AccordionItem value={id} className="bg-background rounded-lg">{t18}{t25}</AccordionItem>;
    $[46] = id;
    $[47] = t18;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== defaultExpanded || $[51] !== t26) {
    t27 = <Accordion type="single" collapsible={true} defaultChecked={defaultExpanded}>{t26}</Accordion>;
    $[50] = defaultExpanded;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  return t27;
}
export { MobileTransaction };